import type React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerCards, cardItem } from '../utils/animations';
import Section from './ui/Section';
import SectionHeading from './ui/SectionHeading';
import Icon from './ui/Icon';
import { COMPANY_INFO } from '../utils/constants';

// Define the principle type with the correct icon type
interface Principle {
  id: string;
  icon: 'transparency' | 'flexibility' | 'experience';
  title: string;
  description: string;
}

interface Audience {
  id: string;
  title: string;
  description: string;
}

const AboutUs: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const yearsInBusiness = currentYear - COMPANY_INFO.foundedYear;

  const principles: Principle[] = [
    {
      id: 'honesty',
      icon: 'transparency',
      title: 'Честность',
      description: 'Прозрачное ценообразование, понятные договоры, мы не навязываем лишнего.',
    },
    {
      id: 'flexibility',
      icon: 'flexibility',
      title: 'Гибкость',
      description: 'Подбираем решения под ваш бюджет и задачи, от разовых проектов до полного аутсорсинга.',
    },
    {
      id: 'expertise',
      icon: 'experience',
      title: 'Экспертиза',
      description: 'Глубокие знания и практический опыт нашей команды гарантируют качество.',
    },
  ];

  const targetAudience: Audience[] = [
    {
      id: 'small-business',
      title: 'Малый бизнес',
      description: 'Надежные IT-решения, которые масштабируются вместе с вашим ростом.',
    },
    {
      id: 'medium-business',
      title: 'Средний бизнес',
      description: 'Комплексная инфраструктура и поддержка для бесперебойной работы.',
    },
    {
      id: 'startups',
      title: 'Стартапы',
      description: 'Быстрый запуск IT-инфраструктуры с учетом будущего развития.',
    },
  ];

  return (
    <Section id="about" bgColor="white">
      <SectionHeading
        title="IT Joy: Ваш надежный IT-партнер с 2005 года"
        subtitle="Мы не просто настраиваем технику – мы строим IT-системы, которые помогают вашему бизнесу расти и работать без сбоев"
      />

      <div className="mb-16 grid gap-8 md:grid-cols-2">
        <motion.div variants={fadeInUp}>
          <h3 className="mb-4 text-2xl font-bold text-primary">О нас</h3>
          <p className="mb-4 text-gray-700">
            С 2005 года мы накопили опыт решения самых разных IT-задач,
            всегда ставя во главу угла честность, гибкость и потребности клиента.
          </p>
          <p className="text-gray-700">
            Наша философия – строить долгосрочные отношения с клиентами,
            понимать их бизнес изнутри и предлагать решения, которые
            действительно работают на результат.
          </p>
        </motion.div>

        <motion.div variants={fadeInUp}>
          <h3 className="mb-4 text-2xl font-bold text-primary">Наша миссия</h3>
          <p className="mb-4 text-gray-700">
            Помогать предпринимателям достигать целей с помощью надежных IT-решений,
            которые работают без сбоев, экономят ресурсы и обеспечивают
            безопасность данных и бизнес-процессов.
          </p>
          <p className="text-gray-700">
            Мы стремимся сделать технологии доступными и понятными
            для наших клиентов, чтобы они могли сфокусироваться
            на развитии своего бизнеса.
          </p>
        </motion.div>
      </div>

      <div className="mb-16">
        <h3 className="mb-6 text-center text-2xl font-bold text-primary">Наши принципы</h3>
        <motion.div
          className="grid gap-6 md:grid-cols-3"
          variants={staggerCards}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {principles.map((principle) => (
            <motion.div
              key={principle.id}
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md"
              variants={cardItem}
            >
              <div className="mb-4 inline-flex rounded-full bg-primary/10 p-3 text-primary">
                <Icon name={principle.icon} size={24} />
              </div>
              <h4 className="mb-2 text-xl font-bold text-gray-900">{principle.title}</h4>
              <p className="text-gray-600">{principle.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <div>
        <h3 className="mb-6 text-center text-2xl font-bold text-primary">Кому мы помогаем</h3>
        <motion.div
          className="grid gap-6 md:grid-cols-3"
          variants={staggerCards}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {targetAudience.map((audience) => (
            <motion.div
              key={audience.id}
              className="rounded-lg bg-primary p-6 text-white"
              variants={cardItem}
            >
              <h4 className="mb-2 text-xl font-bold">{audience.title}</h4>
              <p className="text-gray-200">{audience.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

export default AboutUs;
